import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, arrayMove, sortableKeyboardCoordinates, useSortable, horizontalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import SlidePanel from '../shared/SlidePanel';
import InlinePickerSelect from '../shared/InlinePickerSelect';
import toast from '../shared/Toast';
import { productAction } from '../../_actions/product.action';
import { productService } from '../../_services/product.service';
import '../shared/ui.css';
import '../new-service/ServiceEditorPanel.css';
import LocationScope, { LOCATION_SCOPE_THIS } from '../multiLocation/LocationScope';
import { syncCatalogueAfterSave } from '../multiLocation/syncCatalogue';
import { STOCK_EDIT_VISIBLE } from '../../config/featureFlags';
import RichTextEditor from './RichTextEditor';
import { sanitizeRichText, isRichEmpty } from './richText';
import { compressImages, isHeicFile } from '../shared/imageCompress';
import TaxPicker from '../taxes/TaxPicker';

const STEPS = [
    { id: 'details', number: 1, label: 'Details' },
    { id: 'images', number: 2, label: 'Images' },
    { id: 'pricing', number: 3, label: 'Pricing' },
    { id: 'stock', number: 4, label: 'Stock' },
];

// Product weight is stored canonically in grams (the shipping engine matches by
// gram tiers). The editor lets the user enter it in g / kg / oz / lb and converts.
const WEIGHT_UNITS = [
    { value: 'g', label: 'g', toG: 1 },
    { value: 'kg', label: 'kg', toG: 1000 },
    { value: 'oz', label: 'oz', toG: 28.3495 },
    { value: 'lb', label: 'lb', toG: 453.592 },
];
const weightToGrams = (val, unit) => {
    const n = parseFloat(val);
    if (!isFinite(n) || n <= 0) return 0;
    const u = WEIGHT_UNITS.find((x) => x.value === unit) || WEIGHT_UNITS[0];
    // Keep 2dp of gram precision so oz/lb survive the round-trip
    // (99oz → 2806.6g → 99oz, not 2807g → 99.014oz).
    return Math.round(n * u.toG * 100) / 100;
};
// Re-express stored grams in the unit the salon entered them in (persisted as
// weightUnit); fall back to a friendly derived unit (kg once it's 1kg+).
const gramsToWeight = (grams, savedUnit) => {
    const n = Number(grams) || 0;
    if (n <= 0) return { value: '', unit: savedUnit || 'g' };
    const u = WEIGHT_UNITS.find((x) => x.value === savedUnit);
    if (u) return { value: String(+(n / u.toG).toFixed(3)), unit: u.value };
    if (n >= 1000) return { value: String(+(n / 1000).toFixed(3)), unit: 'kg' };
    return { value: String(n), unit: 'g' };
};

const ProductEditorPanel = ({ product, currency = '£', inline, onBack, onClose, onSaved }) => {
    const dispatch = useDispatch();
    const slidePanelRef = useRef(null);
    const fileInputRef = useRef(null);

    const [activeTab, setActiveTab] = useState('details');
    const [saving, setSaving] = useState(false);
    const [errors, setErrors] = useState({});
    const [dirty, setDirty] = useState(false);

    // Form state — initialised from `product` prop
    const [productId, setProductId] = useState('');
    // Locations control: products default to this location (stock is per site).
    const [locScope, setLocScope] = useState(LOCATION_SCOPE_THIS);
    const [productName, setProductName] = useState('');
    const [productCategory, setProductCategory] = useState(null);
    const [productBrand, setProductBrand] = useState(null);
    const [description, setDescription] = useState('');
    const [productImages, setProductImages] = useState([]);
    const [existingImages, setExistingImages] = useState([]);
    const [deletedImages, setDeletedImages] = useState([]);
    const [retailPrice, setRetailPrice] = useState('');
    const [stockPrice, setStockPrice] = useState('');
    // Tracked but never sold (freebies, samples, salon use): priced at £0, kept off the shop
    const [notForSale, setNotForSale] = useState(false);
    // Stock (STOCK_EDIT_VISIBLE): starting stock on a new product; on an
    // existing one, a new count plus the reason it changed
    const [stockNow, setStockNow] = useState('');
    const [stockReason, setStockReason] = useState('');
    const [commissionEnable, setCommissionEnable] = useState('no');
    const [barcode, setBarcode] = useState('');
    const [sku, setSku] = useState('');
    const [trackStock, setTrackStock] = useState('yes');
    // Retail variants (Colour/Size...): options define the dropdowns, rows are
    // the combinations with per-variant sku/price/stock. Empty = no variants.
    const [variantOptions, setVariantOptions] = useState([]); // [{name, values:[..]}]
    const [variantRows, setVariantRows] = useState([]);       // [{_id?, optionValues, sku, retailPrice, quantity}]
    // Photo per choice of the FIRST option (Shopify-style: one photo per
    // colour). Keyed by value so renaming the option keeps the photos. Only
    // saved gallery photos can be assigned — new uploads get a URL on save.
    const [valueImages, setValueImages] = useState({});       // {value: url}
    const [imagePickerFor, setImagePickerFor] = useState(null);

    // The combination grid writes itself: whenever the options change, the
    // rows regenerate as the cartesian product of the choices, keeping
    // anything already typed for combinations that still exist. No
    // "generate" step for the salon to learn.
    useEffect(() => {
        const opts = variantOptions.filter(o => o.name.trim() && o.values.length);
        if (!opts.length) { setVariantRows((prev) => (prev.length ? [] : prev)); return; }
        setVariantRows((prev) => {
            let combos = [{}];
            opts.forEach(o => { combos = combos.flatMap(c => o.values.map(v => ({ ...c, [o.name]: v }))); });
            if (combos.length > 100) combos = combos.slice(0, 100);
            const keyOf = (ov) => opts.map(o => ov[o.name]).join(' / ');
            const existing = new Map(prev.map(r => [keyOf(r.optionValues), r]));
            const next = combos.map(ov => existing.get(keyOf(ov)) || ({ optionValues: ov, sku: '', retailPrice: '', stockPrice: '', overridePrice: false, quantity: '0' }));
            const same = next.length === prev.length && next.every((r, i) => r === prev[i]);
            return same ? prev : next;
        });
    }, [variantOptions]);
    const [weight, setWeight] = useState('');
    const [weightUnit, setWeightUnit] = useState('g');
    const [sellOnline, setSellOnline] = useState('no');

    const [categoryOptions, setCategoryOptions] = useState([]);
    const [brandOptions, setBrandOptions] = useState([]);

    // --- Load options + populate state from product ---
    useEffect(() => {
        dispatch(productAction.listProductCategories())
            .then((response) => {
                const categories = response?.data?.responseData?.categories || [];
                setCategoryOptions(categories.map(c => ({ value: c.categoryId, label: c.categoryName })));
            })
            .catch(() => toast.error('Failed to load product categories'));

        dispatch(productAction.listProductBrands())
            .then((response) => {
                const brands = response?.data?.responseData?.brands || [];
                setBrandOptions(brands.map(b => ({ value: b.brandId || b._id, label: b.brandName })));
            })
            .catch(() => toast.error('Failed to load product brands'));
    }, [dispatch]);

    useEffect(() => {
        if (!product) return;
        setProductId(product._id || product.id || '');
        setProductName(product.productName || '');
        setDescription(product.description || '');
        // Prices always read as pounds and pence (3.5 → 3.50)
        const twoDp = (v) => (v === undefined || v === null || v === '' || !Number.isFinite(Number(v)) ? '' : Number(v).toFixed(2));
        setRetailPrice(twoDp(product.retailPrice) === '0.00' ? '' : twoDp(product.retailPrice));
        setStockPrice(twoDp(product.stockPrice) === '0.00' ? '' : twoDp(product.stockPrice));
        setStockNow(product.quantity != null ? String(Math.max(0, parseInt(product.quantity, 10) || 0)) : '');
        setStockReason('');
        setNotForSale(product.retailPrice !== undefined && product.retailPrice !== null && Number(product.retailPrice) === 0);
        setCommissionEnable(product.commissionEnable === 1 ? 'yes' : 'no');
        setBarcode(product.barcode || '');
        setSku(product.sku || '');
        setTrackStock(product.trackStock === 1 ? 'yes' : 'no');
        { const w = gramsToWeight(product.weight, product.weightUnit); setWeight(w.value); setWeightUnit(w.unit); }
        setSellOnline(product.sellOnline === 1 ? 'yes' : 'no');
        setVariantOptions((product.options || []).map(o => ({ name: o.name, values: [...(o.values || [])] })));
        setVariantRows((product.variants || []).map(v => ({
            _id: v._id, optionValues: v.optionValues || {},
            sku: v.sku || '', retailPrice: v.retailPrice != null ? String(v.retailPrice) : '',
            stockPrice: v.stockPrice != null ? String(v.stockPrice) : '',
            // null prices = inherits the product's; a set price = override on
            overridePrice: v.retailPrice != null || v.stockPrice != null,
            quantity: v.quantity != null ? String(v.quantity) : '0',
        })));
        {
            const firstOpt = (product.options || [])[0];
            const vi = {};
            if (firstOpt) {
                (product.variants || []).forEach(v => {
                    const val = v.optionValues && v.optionValues[firstOpt.name];
                    if (val && v.image && !vi[val]) vi[val] = v.image;
                });
            }
            setValueImages(vi);
        }
        setImagePickerFor(null);
        if (product.images && product.images.length > 0) {
            setExistingImages(product.images.map((img, index) => ({
                id: img._id || `existing-${index}`,
                url: img.url,
                key: img.key,
                order: index,
            })));
        } else {
            setExistingImages([]);
        }
    }, [product]);

    useEffect(() => {
        if (!product || categoryOptions.length === 0) return;
        if (product.categoryId) {
            const match = categoryOptions.find(c => c.value === product.categoryId);
            if (match) setProductCategory(match);
            else if (product.category) setProductCategory({ value: product.category._id, label: product.category.categoryName });
        }
    }, [product, categoryOptions]);

    useEffect(() => {
        if (!product || brandOptions.length === 0) return;
        if (product.brandId) {
            const match = brandOptions.find(b => b.value === product.brandId);
            if (match) setProductBrand(match);
            else if (product.brand) setProductBrand({ value: product.brand._id, label: product.brand.brandName });
        }
    }, [product, brandOptions]);

    // --- Validation ---
    const validateFields = (name, value) => {
        switch (name) {
            case 'productName':
                if (!value) return 'Product Name is required.';
                break;
            case 'productCategory':
                if (!value) return 'Product Category is required.';
                break;
            case 'productBrand':
                // FE-side requirement: placeholder until the backend allows an empty brandId.
                if (!value) return 'Product Brand is required.';
                break;
            case 'retailPrice':
                if (notForSale) return '';
                if (!value) return 'Retail Price is required.';
                if (isNaN(value)) return 'Retail Price must be a number.';
                break;
            case 'stockPrice':
                // Optional: left blank, it's saved as £0.00
                if (value === '' || value == null) return '';
                if (isNaN(value)) return 'Stock Price must be a number.';
                if (!notForSale && parseFloat(value) > parseFloat(retailPrice)) return 'Stock Price cannot be greater than Retail Price.';
                break;
            default: return '';
        }
        return '';
    };

    // --- Input handlers ---
    const handleChange = (e) => {
        const { name, value } = e.target;
        switch (name) {
            case 'productName': setProductName(value); break;
            case 'retailPrice': if (!isNaN(value)) setRetailPrice(value); break;
            case 'stockPrice': if (!isNaN(value)) setStockPrice(value); break;
            case 'description': setDescription(value); break;
            case 'barcode': setBarcode(value); break;
            case 'sku': setSku(value); break;
            default: break;
        }
        setErrors(prev => ({ ...prev, [name]: validateFields(name, value) }));
        setDirty(true);
    };

    const handleCategoryChange = (selected) => {
        setProductCategory(selected);
        setErrors(prev => ({ ...prev, productCategory: validateFields('productCategory', selected) }));
        setDirty(true);
    };

    const handleBrandChange = (selected) => {
        setProductBrand(selected);
        setErrors(prev => ({ ...prev, productBrand: validateFields('productBrand', selected) }));
        setDirty(true);
    };

    // --- Image handling ---
    const handleImageUpload = async (e) => {
      try {
        const files = Array.from(e.target.files);
        // Allow large source photos here (they're optimised below before upload).
        const validFiles = files.filter(file => {
            const isValidType = ['image/jpeg', 'image/png', 'image/webp', 'image/heic', 'image/heif'].includes(file.type) || isHeicFile(file);
            const isValidSize = file.size <= 25 * 1024 * 1024;
            if (!isValidType) toast.error(`${file.name} is not a supported image format.`);
            if (!isValidSize) toast.error(`${file.name} is too large (max 25MB).`);
            return isValidType && isValidSize;
        });
        if (validFiles.length === 0) return;
        // Downscale/re-encode big images so they fit under the upload size limit.
        let processed = validFiles;
        try { processed = await compressImages(validFiles); } catch (err) { processed = validFiles; }
    // A HEIC that survived compression means this browser can't decode iPhone
    // photos (WebKit can; Chrome/Windows can't). Say so instead of uploading
    // a file the storefront can never display.
    processed = processed.filter((f) => {
      if (isHeicFile(f)) {
        toast.error(`${f.name} is an iPhone photo in HEIC format, which browsers can't read. Pick it from your Photo Library instead (it converts automatically), or use a JPG/PNG copy.`);
        return false;
      }
      return true;
    });
    if (processed.length === 0) return;
        const newImages = processed.map(file => ({
            id: `new-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
            file,
            preview: URL.createObjectURL(file),
            order: existingImages.length + productImages.length,
        }));
        setProductImages(prev => [...prev, ...newImages]);
        if (newImages.length > 0) setDirty(true);
      } catch (err) {
        // iOS Safari can invalidate picked files mid-processing; say so
        // instead of doing nothing.
        console.error('Image add failed', err);
        toast.error('Could not read that photo. Please try again.');
      }
    };

    const handleRemoveImage = (index) => {
        setProductImages(prev => {
            const next = [...prev];
            URL.revokeObjectURL(next[index].preview);
            next.splice(index, 1);
            return next;
        });
        setDirty(true);
    };

    const handleRemoveExistingImage = (index) => {
        const imageToRemove = existingImages[index];
        setDeletedImages(prev => [...prev, imageToRemove]);
        setExistingImages(prev => {
            const next = [...prev];
            next.splice(index, 1);
            return next;
        });
        setDirty(true);
    };

    const handleScanBarcode = () => {
        toast.info('Barcode scanner functionality would be implemented here');
    };

    // --- Drag and drop sensors + reorder ---
    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
        useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
    );

    const allImages = [
        ...existingImages.map(img => ({ ...img, isExisting: true })),
        ...productImages.map(img => ({ ...img, isExisting: false })),
    ];

    const handleAllImagesDragEnd = (event) => {
        const { active, over } = event;
        if (!over || active.id === over.id) return;
        const oldIndex = allImages.findIndex(item => item.id === active.id);
        const newIndex = allImages.findIndex(item => item.id === over.id);
        const next = arrayMove([...allImages], oldIndex, newIndex);
        setExistingImages(next.filter(i => i.isExisting).map(i => ({ ...i, isExisting: undefined })));
        setProductImages(next.filter(i => !i.isExisting).map(i => ({ ...i, isExisting: undefined })));
        setDirty(true);
    };

    const SortableImageItem = ({ image, index }) => {
        const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: image.id });
        const style = { transform: CSS.Transform.toString(transform), transition };
        const isExisting = image.isExisting;
        return (
            <div ref={setNodeRef} style={{ ...style, cursor: 'grab' }} className="sep-image-item" {...attributes} {...listeners}>
                <img src={isExisting ? image.url : image.preview} alt={`Product ${index}`} />
                <button
                    type="button"
                    className="sep-image-delete"
                    onClick={(e) => {
                        e.stopPropagation();
                        if (isExisting) handleRemoveExistingImage(existingImages.findIndex(img => img.id === image.id));
                        else handleRemoveImage(productImages.findIndex(img => img.id === image.id));
                    }}
                >×</button>
            </div>
        );
    };

    // --- Next (new products): check this step's fields, then the next step ---
    const stepIndex = Math.max(0, STEPS.findIndex(st => st.id === activeTab));
    const STEP_FIELDS = {
        details: () => ({
            productName: validateFields('productName', productName),
            productCategory: validateFields('productCategory', productCategory),
            productBrand: validateFields('productBrand', productBrand),
        }),
        pricing: () => ({
            retailPrice: validateFields('retailPrice', retailPrice),
            stockPrice: validateFields('stockPrice', stockPrice),
        }),
    };
    const goNext = () => {
        const check = STEP_FIELDS[activeTab];
        if (check) {
            const found = Object.fromEntries(Object.entries(check()).filter(([, v]) => v));
            setErrors(prev => ({ ...prev, ...check(), ...found }));
            if (Object.keys(found).length) return;
        }
        const next = STEPS[stepIndex + 1];
        if (next) setActiveTab(next.id);
    };

    // --- Stock (STOCK_EDIT_VISIBLE) ---
    const hasOptions = variantOptions.some(o => o.name.trim() && o.values.length);
    const stockEditable = STOCK_EDIT_VISIBLE && trackStock === 'yes' && !hasOptions;
    const stockWas = product && product.quantity != null ? Math.max(0, parseInt(product.quantity, 10) || 0) : 0;
    const stockChange = productId && stockEditable && stockNow !== '' ? (parseInt(stockNow, 10) || 0) - stockWas : 0;

    // --- Save ---
    const handleSave = useCallback(() => {
        if (!dirty) {
            if (slidePanelRef.current?.handleClose) slidePanelRef.current.handleClose();
            setTimeout(() => onClose && onClose(), 280);
            return;
        }
        const newErrors = {
            productName: validateFields('productName', productName),
            productCategory: validateFields('productCategory', productCategory),
            productBrand: validateFields('productBrand', productBrand),
            retailPrice: validateFields('retailPrice', retailPrice),
            stockPrice: validateFields('stockPrice', stockPrice),
        };
        const filtered = Object.fromEntries(Object.entries(newErrors).filter(([_, v]) => v));
        setErrors(filtered);
        if (Object.keys(filtered).length > 0) {
            if (filtered.productName || filtered.productCategory || filtered.productBrand) setActiveTab('details');
            else if (filtered.retailPrice || filtered.stockPrice) setActiveTab('pricing');
            return;
        }
        if (stockChange !== 0 && !stockReason) {
            setErrors(prev => ({ ...prev, stockReason: 'Say why the stock changed.' }));
            setActiveTab('stock');
            return;
        }
        if (!notForSale && parseFloat(stockPrice) > parseFloat(retailPrice)) {
            toast.error('Stock price cannot be greater than retail price.');
            setActiveTab('pricing');
            return;
        }
        const isEdit = !!productId;

        const formData = new FormData();
        if (isEdit) formData.append('productId', String(productId));
        formData.append('productName', productName);
        formData.append('retailPrice', notForSale ? 0 : parseFloat(retailPrice));
        formData.append('stockPrice', stockPrice === '' || stockPrice == null ? 0 : (parseFloat(stockPrice) || 0));
        formData.append('commissionEnable', notForSale || commissionEnable === 'no' ? 0 : 1);
        formData.append('description', isRichEmpty(description) ? '' : sanitizeRichText(description));
        if (productCategory) formData.append('categoryId', productCategory.value);
        if (productBrand) formData.append('brandId', productBrand.value);
        formData.append('barcode', barcode);
        formData.append('sku', sku);
        formData.append('trackStock', trackStock === 'yes' ? 1 : 0);
        // Starting stock on a new product (the server must accept `quantity`)
        if (!isEdit && stockEditable) formData.append('quantity', Math.max(0, parseInt(stockNow, 10) || 0));
        formData.append('weight', weightToGrams(weight, weightUnit));
        formData.append('weightUnit', weightUnit);
        formData.append('sellOnline', !notForSale && sellOnline === 'yes' ? 1 : 0);
        // Variants ride as JSON strings in the multipart body; empty clears.
        // Each row's photo comes from the per-value assignment on option 1.
        const cleanOptions = variantOptions.filter(o => o.name.trim() && o.values.length);
        const firstOptName = cleanOptions.length ? cleanOptions[0].name : null;
        const rowsToSave = cleanOptions.length
            ? variantRows.map(r => ({ ...r, image: (firstOptName && valueImages[r.optionValues[firstOptName]]) || '' }))
            : [];
        formData.append('options', JSON.stringify(cleanOptions));
        formData.append('variants', JSON.stringify(rowsToSave));
        if (isEdit) {
            formData.append('existingImages', JSON.stringify(existingImages.map((img, i) => ({ ...img, order: i }))));
            formData.append('deletedImages', JSON.stringify(deletedImages));
        }
        const allOrder = isEdit
            ? [
                ...existingImages.map((img, i) => ({ id: img.id, isExisting: true, order: i })),
                ...productImages.map((img, i) => ({ id: img.id, isExisting: false, order: existingImages.length + i })),
            ]
            : productImages.map((img, i) => ({ id: img.id, order: i }));
        formData.append('imageOrder', JSON.stringify(allOrder));
        productImages.forEach(img => formData.append('images', img.file));

        setSaving(true);
        const request = isEdit
            ? dispatch(productAction.updateProduct(formData))
            : dispatch(productAction.addProduct(formData));
        request
            .then((response) => {
                // The API returns HTTP 200 even on validation failure, signalling the
                // real outcome via the response body (statusCode 0 + error). Treat that
                // as a failure rather than reporting a false success.
                const data = response?.data;
                if (data && (data.statusCode === 0 || data.error)) {
                    const apiError = data.error?.errors?.[0]?.message
                        || data.error?.responseMessage
                        || 'Validation error.';
                    toast.error((isEdit ? 'Failed to update product: ' : 'Failed to add product: ') + apiError);
                    setSaving(false);
                    return;
                }
                toast.success(isEdit ? 'Product Updated Successfully' : 'Product Added Successfully');
                // Locations control: create at chosen sites (zero stock) or update their shared fields.
                const savedId = isEdit ? productId : (data?.responseData?.result?._id || data?.responseData?.result?.id);
                if (savedId) syncCatalogueAfterSave('product', String(savedId), locScope);
                // A changed count on an existing product goes in as an adjustment with its reason
                if (isEdit && stockChange !== 0) {
                    productService.adjustStock({ productId: String(productId), change: stockChange, reason: stockReason })
                        .then((r) => toast.success(`Stock is now ${r && r.quantity != null ? r.quantity : (parseInt(stockNow, 10) || 0)}`))
                        .catch((e) => toast.error(`Stock not changed: ${(e && e.message) || 'please try again'}`));
                }
                setSaving(false);
                setDirty(false);
                if (onSaved) onSaved();
            })
            .catch((err) => {
                toast.error((isEdit ? 'Failed to update product: ' : 'Failed to add product: ') + (err?.message || 'Unknown error'));
                setSaving(false);
            });
    }, [dirty, productId, productName, productCategory, productBrand, description, retailPrice, stockPrice, notForSale, commissionEnable, barcode, sku, trackStock, weight, weightUnit, sellOnline, variantOptions, variantRows, valueImages, existingImages, productImages, deletedImages, stockEditable, stockNow, stockChange, stockReason, dispatch, onSaved, onClose]);

    const handleClose = () => {
        if (dirty && !window.confirm('You have unsaved changes. Discard them?')) return;
        if (inline && onBack) {
            onBack();
            return;
        }
        if (slidePanelRef.current?.handleClose) slidePanelRef.current.handleClose();
        setTimeout(() => onClose && onClose(), 280);
    };

    // --- Render sections ---
    const renderDetails = () => (
        <div className="sep-section">
            <p className="sep-section-label">Product Details</p>
            <LocationScope value={locScope} onChange={setLocScope} help="Created at zero stock elsewhere; stock only moves by transfer." />

            <div className="sep-field">
                <label className="sep-field-label">Product Name</label>
                <input
                    type="text"
                    name="productName"
                    placeholder="e.g. Shampoo"
                    className={`sep-input ${errors.productName ? 'sep-input--error' : ''}`}
                    value={productName}
                    onChange={handleChange}
                />
                {errors.productName && <span className="sep-error">{errors.productName}</span>}
            </div>

            <div className="sep-field">
                <label className="sep-field-label">Category</label>
                <InlinePickerSelect
                    options={categoryOptions}
                    value={productCategory}
                    onChange={handleCategoryChange}
                    onCreateNew={async (name) => {
                        try {
                            const response = await productService.addProductCategory({ categoryName: name });
                            const rd = response?.data?.responseData || response?.data?.data || response?.data || {};
                            const newCat = rd.category || rd.result || rd;
                            const catId = newCat.categoryId || newCat._id || newCat.id;
                            if (catId) {
                                const option = { value: catId, label: name };
                                setCategoryOptions(prev => [...prev, option]);
                                handleCategoryChange(option);
                                toast.success('Category created');
                            } else {
                                const res = await dispatch(productAction.listProductCategories());
                                const cats = res?.data?.responseData?.categories || [];
                                const formatted = cats.map(c => ({ value: c.categoryId, label: c.categoryName }));
                                setCategoryOptions(formatted);
                                const match = formatted.find(c => c.label === name);
                                if (match) handleCategoryChange(match);
                                toast.success('Category created');
                            }
                        } catch { toast.error('Failed to create category'); throw new Error(); }
                    }}
                    placeholder="Choose a Category"
                    createLabel="Create new category"
                    searchPlaceholder="Search categories..."
                    error={errors.productCategory}
                />
            </div>

            <div className="sep-field">
                <label className="sep-field-label">Brand</label>
                <InlinePickerSelect
                    options={brandOptions}
                    value={productBrand}
                    onChange={handleBrandChange}
                    onCreateNew={async (name) => {
                        try {
                            const response = await productService.addProductBrand({ brandName: name });
                            const rd = response?.data?.responseData || response?.data?.data || response?.data || {};
                            const newBr = rd.brand || rd.result || rd;
                            const brId = newBr.brandId || newBr._id || newBr.id;
                            if (brId) {
                                const option = { value: brId, label: name };
                                setBrandOptions(prev => [...prev, option]);
                                handleBrandChange(option);
                                toast.success('Brand created');
                            } else {
                                const res = await dispatch(productAction.listProductBrands());
                                const brands = res?.data?.responseData?.brands || [];
                                const formatted = brands.map(b => ({ value: b.brandId || b._id, label: b.brandName }));
                                setBrandOptions(formatted);
                                const match = formatted.find(b => b.label === name);
                                if (match) handleBrandChange(match);
                                toast.success('Brand created');
                            }
                        } catch { toast.error('Failed to create brand'); throw new Error(); }
                    }}
                    placeholder="Choose a Brand"
                    createLabel="Create new brand"
                    searchPlaceholder="Search brands..."
                    error={errors.productBrand}
                />
            </div>

            <div className="sep-field">
                <label className="sep-field-label">Description</label>
                <RichTextEditor
                    value={description}
                    onChange={(html) => { setDescription(html); setDirty(true); }}
                    placeholder="Describe this product. Use bold and bullet points to make it easy to read; it shows in your online shop."
                />
            </div>
        </div>
    );

    const renderImages = () => (
        <div className="sep-section">
            <p className="sep-section-label">Images</p>
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleAllImagesDragEnd}>
                <SortableContext items={allImages.map(img => img.id)} strategy={horizontalListSortingStrategy}>
                    <div className="sep-images-grid">
                        {allImages.map((image, index) => (
                            <SortableImageItem key={image.id} image={image} index={index} />
                        ))}
                        <div className="sep-image-upload" style={{ position: 'relative' }} onClick={() => fileInputRef.current && fileInputRef.current.click()}>
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                                <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
                            </svg>
                            <span>Add</span>
                            <input
                                type="file"
                                ref={fileInputRef}
                                accept="image/jpeg, image/png, image/webp"
                                multiple
                                onClick={(e) => {
                                    // The invisible input covers its wrapper tile, whose own
                                    // onClick calls input.click() — without stopPropagation the
                                    // bubble re-clicks the input and WebKit (Safari/iOS) opens a
                                    // SECOND file dialog, wiping the first pick. Chrome suppresses
                                    // the re-entrant dialog, which is why it only broke on Safari.
                                    e.stopPropagation();
                                    e.target.value = '';
                                }}
                                onChange={handleImageUpload}
                                style={{ position: 'absolute', inset: 0, opacity: 0, cursor: 'pointer' }}
                            />
                        </div>
                    </div>
                </SortableContext>
            </DndContext>
            <p style={{ fontSize: '12px', color: '#8e8e93', marginTop: '8px' }}>
                JPG, PNG or WEBP. Large photos are optimised automatically. {allImages.length > 1 ? 'Drag to reorder.' : ''}
            </p>
        </div>
    );

    const renderPricing = () => (
        <div className="sep-section">
            <p className="sep-section-label">Pricing</p>

            <div className="sep-toggle-row">
                <div className="sep-toggle-info">
                    <div className="sep-toggle-title">Not for sale</div>
                    <div className="sep-toggle-desc">Freebies, samples or salon use. Track the stock without selling it: it's priced at {currency}0.00 and kept out of your online shop.</div>
                </div>
                <button
                    type="button"
                    className={`sep-toggle ${notForSale ? 'sep-toggle--on' : ''}`}
                    aria-pressed={notForSale}
                    onClick={() => { setNotForSale(!notForSale); setErrors(prev => ({ ...prev, retailPrice: '', stockPrice: '' })); setDirty(true); }}
                >
                    <span className="sep-toggle-knob" />
                </button>
            </div>

            <div className="sep-field-row">
                {!notForSale && <div className="sep-field">
                    <label className="sep-field-label">Retail Price ({currency})</label>
                    <input
                        type="text"
                        name="retailPrice"
                        placeholder="0.00"
                        className={`sep-input ${errors.retailPrice ? 'sep-input--error' : ''}`}
                        value={retailPrice}
                        onChange={handleChange}
                        inputMode="decimal"
                        onBlur={() => { const n = parseFloat(retailPrice); if (retailPrice !== '' && Number.isFinite(n)) setRetailPrice(n.toFixed(2)); }}
                    />
                    <span className="sep-field-hint">The price the item is sold at</span>
                    {errors.retailPrice && <span className="sep-error">{errors.retailPrice}</span>}
                </div>}
                {productId ? <TaxPicker domain="products" itemId={productId} /> : null}
                <div className="sep-field">
                    <label className="sep-field-label">Stock Price ({currency}) <span className="sep-optional">optional</span></label>
                    <input
                        type="text"
                        name="stockPrice"
                        placeholder="0.00"
                        className={`sep-input ${errors.stockPrice ? 'sep-input--error' : ''}`}
                        value={stockPrice}
                        onChange={handleChange}
                        inputMode="decimal"
                        onBlur={() => { const n = parseFloat(stockPrice); if (stockPrice !== '' && Number.isFinite(n)) setStockPrice(n.toFixed(2)); }}
                    />
                    <span className="sep-field-hint">What it costs the business. Left blank, it's {currency}0.00</span>
                    {errors.stockPrice && <span className="sep-error">{errors.stockPrice}</span>}
                    {!notForSale && parseFloat(stockPrice) > parseFloat(retailPrice) && (
                        <span className="sep-error">Stock price cannot be greater than retail price.</span>
                    )}
                </div>
            </div>

            {!notForSale && <div className="sep-toggle-row" style={{ borderBottom: 'none' }}>
                <div className="sep-toggle-info">
                    <div className="sep-toggle-title">Commission enabled</div>
                    <div className="sep-toggle-desc">Pay staff a commission when this product sells</div>
                </div>
                <button
                    type="button"
                    className={`sep-toggle ${commissionEnable === 'yes' ? 'sep-toggle--on' : ''}`}
                    onClick={() => { setCommissionEnable(commissionEnable === 'yes' ? 'no' : 'yes'); setDirty(true); }}
                >
                    <span className="sep-toggle-knob" />
                </button>
            </div>}
        </div>
    );

    const renderStock = () => (
        <div className="sep-section">
            <p className="sep-section-label">In stock</p>

            <div className="sep-field-row">
                <div className="sep-field">
                    <label className="sep-field-label">Barcode</label>
                    <div style={{ display: 'flex', gap: '8px' }}>
                        <input
                            type="text"
                            name="barcode"
                            placeholder="Enter barcode"
                            className="sep-input"
                            value={barcode}
                            onChange={handleChange}
                            style={{ flex: 1 }}
                        />
                        <button
                            type="button"
                            onClick={handleScanBarcode}
                            style={{
                                width: '44px', height: '44px', borderRadius: '10px',
                                border: '1px solid #e0e0e0', background: '#fff', cursor: 'pointer',
                                display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                            }}
                            aria-label="Scan barcode"
                        >
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <rect x="3" y="5" width="3" height="14" rx="1" stroke="#333" strokeWidth="2"/>
                                <rect x="8" y="5" width="2" height="14" rx="1" stroke="#333" strokeWidth="2"/>
                                <rect x="12" y="5" width="1" height="14" rx="0.5" stroke="#333" strokeWidth="1"/>
                                <rect x="15" y="5" width="1" height="14" rx="0.5" stroke="#333" strokeWidth="1"/>
                                <rect x="18" y="5" width="3" height="14" rx="1" stroke="#333" strokeWidth="2"/>
                            </svg>
                        </button>
                    </div>
                </div>
                <div className="sep-field">
                    <label className="sep-field-label">SKU</label>
                    <input
                        type="text"
                        name="sku"
                        placeholder="Enter SKU"
                        className="sep-input"
                        value={sku}
                        onChange={handleChange}
                    />
                </div>
                <div className="sep-field">
                    <label className="sep-field-label">Weight</label>
                    <div style={{ display: 'flex', gap: '8px' }}>
                        <input
                            type="number"
                            name="weight"
                            min="0"
                            step="any"
                            placeholder="e.g. 250"
                            className="sep-input"
                            value={weight}
                            onChange={(e) => { setWeight(e.target.value); setDirty(true); }}
                            style={{ flex: 1 }}
                        />
                        <select
                            className="sep-input"
                            value={weightUnit}
                            onChange={(e) => { setWeightUnit(e.target.value); setDirty(true); }}
                            aria-label="Weight unit"
                            style={{ flex: '0 0 76px', width: '76px' }}
                        >
                            {WEIGHT_UNITS.map((u) => <option key={u.value} value={u.value}>{u.label}</option>)}
                        </select>
                    </div>
                    <span className="sep-field-hint">Used for weight-based delivery rates. Leave blank if you don't charge by weight.</span>
                </div>
            </div>

            <div className="sep-toggle-row">
                <div className="sep-toggle-info">
                    <div className="sep-toggle-title">Track stock</div>
                    <div className="sep-toggle-desc">Update stock levels automatically when sold</div>
                </div>
                <button
                    type="button"
                    className={`sep-toggle ${trackStock === 'yes' ? 'sep-toggle--on' : ''}`}
                    onClick={() => { setTrackStock(trackStock === 'yes' ? 'no' : 'yes'); setDirty(true); }}
                >
                    <span className="sep-toggle-knob" />
                </button>
            </div>

            {stockEditable && (
                <div className="sep-stock-now">
                    <div className="sep-stock-now-row">
                        <div>
                            <label className="sep-field-label" htmlFor="sep-stock-now">In stock now</label>
                            <span className="sep-field-hint">{productId ? `Was ${stockWas}. Sales take it down from here.` : 'How many you have today. Sales take it down from here.'}</span>
                        </div>
                        <div className="sep-stepper">
                            <button type="button" aria-label="One fewer" onClick={() => { setStockNow(String(Math.max(0, (parseInt(stockNow, 10) || 0) - 1))); setDirty(true); }}>−</button>
                            <input id="sep-stock-now" className="sep-input" inputMode="numeric" placeholder="0" value={stockNow}
                                onChange={(e) => { const v = e.target.value.replace(/[^0-9]/g, ''); setStockNow(v); setDirty(true); }} />
                            <button type="button" aria-label="One more" onClick={() => { setStockNow(String((parseInt(stockNow, 10) || 0) + 1)); setDirty(true); }}>+</button>
                        </div>
                    </div>
                    {stockChange !== 0 && (
                        <div className="sep-stock-why">
                            <span className="sep-field-label">{stockChange > 0 ? `${stockChange} more` : `${-stockChange} fewer`}. Why?</span>
                            <div className="sep-reason-chips">
                                {(stockChange > 0
                                    ? ['Delivery in', 'Returned', 'Count correction']
                                    : ['Damaged', 'Used in the salon', 'Given away', 'Lost or stolen', 'Count correction']
                                ).map(r => (
                                    <button key={r} type="button" className={`sep-reason-chip ${stockReason === r ? 'is-on' : ''}`}
                                        onClick={() => { setStockReason(r); setErrors(prev => ({ ...prev, stockReason: '' })); setDirty(true); }}>{r}</button>
                                ))}
                            </div>
                            {errors.stockReason && <span className="sep-error">{errors.stockReason}</span>}
                        </div>
                    )}
                </div>
            )}

            {/* Variants — plain-English builder. Options define the shop
                dropdowns; the combination grid below writes itself. */}
            <div style={{ padding: '14px 0', borderBottom: '1px solid #f0f0f0' }}>
                <div className="sep-toggle-info" style={{ marginBottom: 10 }}>
                    <div className="sep-toggle-title">Variants</div>
                    <div className="sep-toggle-desc">For products that come in more than one version, like a T-shirt in different colours and sizes. Clients pick from dropdowns in your shop, and each version has its own stock.</div>
                </div>

                {variantOptions.length === 0 && (
                    <button type="button"
                        style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', justifyContent: 'center', background: '#f8fafc', border: '1px dashed #cbd5e1', borderRadius: 10, padding: '12px', fontSize: 14, fontWeight: 600, color: '#007ea0', cursor: 'pointer' }}
                        onClick={() => { setVariantOptions([{ name: '', values: [] }]); setDirty(true); }}>
                        + This product comes in different options
                    </button>
                )}

                {variantOptions.map((opt, oi) => (
                    <div key={oi} style={{ background: '#f8fafc', border: '1px solid #eef2f6', borderRadius: 12, padding: '12px 14px', marginBottom: 10 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                            <span style={{ fontSize: 12, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Option {oi + 1}</span>
                            <button type="button" style={{ background: 'none', border: 'none', color: '#b91c1c', fontSize: 13, cursor: 'pointer', padding: 0 }}
                                onClick={() => { setVariantOptions(variantOptions.filter((_, i) => i !== oi)); setDirty(true); }}>
                                Remove
                            </button>
                        </div>
                        <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 4 }}>What is the option called?</label>
                        <input className="sep-input" style={{ width: '100%', marginBottom: 10 }} placeholder="e.g. Colour" value={opt.name}
                            onChange={(e) => { const next = [...variantOptions]; next[oi] = { ...opt, name: e.target.value }; setVariantOptions(next); setDirty(true); }} />
                        <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 4 }}>The choices, separated by commas</label>
                        <input className="sep-input" style={{ width: '100%' }} placeholder="e.g. Stone, Black, White" value={opt.valuesText !== undefined ? opt.valuesText : opt.values.join(', ')}
                            onChange={(e) => {
                                const next = [...variantOptions];
                                next[oi] = { ...opt, valuesText: e.target.value, values: e.target.value.split(',').map(v => v.trim()).filter(Boolean) };
                                setVariantOptions(next); setDirty(true);
                            }} />
                        {opt.values.length > 0 && (
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
                                {opt.values.map((v) => (
                                    <span key={v} style={{ padding: '3px 10px', borderRadius: 999, background: '#e8f0fe', color: '#1a56db', fontSize: 12, fontWeight: 600 }}>{v}</span>
                                ))}
                            </div>
                        )}
                    </div>
                ))}

                {variantOptions.length > 0 && variantOptions.length < 3 && (
                    <button type="button" style={{ background: 'none', border: 'none', color: '#007ea0', fontSize: 13, fontWeight: 600, padding: 0, cursor: 'pointer' }}
                        onClick={() => { setVariantOptions([...variantOptions, { name: '', values: [] }]); setDirty(true); }}>
                        + Add another option (like Size)
                    </button>
                )}

                {variantRows.length > 0 && (
                    <div style={{ marginTop: 14 }}>
                        <p style={{ fontSize: 13, color: '#374151', margin: '0 0 8px' }}>
                            Every combination below is its own item, with its own stock.
                            Each one charges the product price{retailPrice ? ` (£${parseFloat(retailPrice).toFixed(2)})` : ''} unless you set a different one.
                        </p>
                        <div style={{ overflowX: 'auto', border: '1px solid #eef2f6', borderRadius: 12 }}>
                            <table style={{ width: '100%', fontSize: 13, borderCollapse: 'collapse' }}>
                                <thead>
                                    <tr style={{ color: '#6b7280', textAlign: 'left', background: '#f8fafc' }}>
                                        <th style={{ padding: '10px 12px', fontWeight: 600 }}>Variant</th>
                                        <th style={{ padding: '10px 12px', fontWeight: 600 }}>Price</th>
                                        <th style={{ padding: '10px 12px', fontWeight: 600 }}>In stock <span style={{ fontWeight: 400, color: '#9ca3af' }}>(set by stock take)</span></th>
                                        <th style={{ padding: '10px 12px', fontWeight: 600 }}>SKU <span style={{ fontWeight: 400 }}>(optional)</span></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {variantRows.map((row, ri) => (
                                        <tr key={ri} style={{ borderTop: '1px solid #f0f0f0' }}>
                                            <td style={{ padding: '8px 12px', fontWeight: 600, whiteSpace: 'nowrap' }}>
                                                {variantOptions.filter(o => o.name.trim()).map(o => row.optionValues[o.name]).filter(Boolean).join(' / ')}
                                            </td>
                                            <td style={{ padding: '6px 12px', verticalAlign: 'top' }}>
                                                {/* Prices inherit from the product; an explicit override
                                                    reveals the inputs. Blank inputs save as null = inherit. */}
                                                {!row.overridePrice ? (
                                                    <div style={{ whiteSpace: 'nowrap', paddingTop: 8 }}>
                                                        <span style={{ fontSize: 13, color: '#374151' }}>{retailPrice ? `£${parseFloat(retailPrice).toFixed(2)}` : 'Not set'}</span>
                                                        <span style={{ fontSize: 11, color: '#9ca3af', marginLeft: 6 }}>product price</span>
                                                        <button type="button"
                                                            style={{ background: 'none', border: 'none', color: '#007ea0', fontSize: 12, fontWeight: 600, cursor: 'pointer', padding: 0, marginLeft: 10 }}
                                                            onClick={() => { const n = [...variantRows]; n[ri] = { ...row, overridePrice: true }; setVariantRows(n); }}>
                                                            Set different price
                                                        </button>
                                                    </div>
                                                ) : (
                                                    <div style={{ display: 'grid', gap: 6 }}>
                                                        {[
                                                            { key: 'retailPrice', label: 'Price', base: retailPrice },
                                                            { key: 'stockPrice', label: 'Cost', base: stockPrice },
                                                        ].map(({ key, label, base }) => (
                                                            <div key={key} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                                <span style={{ fontSize: 11, color: '#6b7280', width: 32 }}>{label}</span>
                                                                <div style={{ position: 'relative', width: 104 }}>
                                                                    <span style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#9ca3af', fontSize: 13 }}>£</span>
                                                                    <input className="sep-input" type="number" min="0" step="0.01" style={{ width: '100%', paddingLeft: 24 }}
                                                                        placeholder={base ? parseFloat(base).toFixed(2) : ''}
                                                                        value={row[key]}
                                                                        onChange={(e) => { const n = [...variantRows]; n[ri] = { ...row, [key]: e.target.value }; setVariantRows(n); setDirty(true); }} />
                                                                </div>
                                                            </div>
                                                        ))}
                                                        <button type="button"
                                                            style={{ background: 'none', border: 'none', color: '#6b7280', fontSize: 12, cursor: 'pointer', padding: 0, justifySelf: 'start' }}
                                                            onClick={() => { const n = [...variantRows]; n[ri] = { ...row, overridePrice: false, retailPrice: '', stockPrice: '' }; setVariantRows(n); setDirty(true); }}>
                                                            Use product price
                                                        </button>
                                                    </div>
                                                )}
                                            </td>
                                            <td style={{ padding: '6px 12px' }}>
                                                {/* Read-only on purpose. Stock is owned by the stock take and
                                                    the till, exactly as it is for a product without variants —
                                                    which has no stock box here at all. Typing a count straight
                                                    in would bypass the stock take's audit trail. */}
                                                {STOCK_EDIT_VISIBLE && !productId && trackStock === 'yes' ? (
                                                    // A new product's starting stock per option (sent in the variants JSON)
                                                    <input className="sep-input" style={{ width: 80 }} inputMode="numeric" placeholder="0" aria-label="Starting stock" value={row.quantity === '0' ? '' : row.quantity}
                                                        onChange={(e) => { const v = e.target.value.replace(/[^0-9]/g, ''); const n = [...variantRows]; n[ri] = { ...row, quantity: v || '0' }; setVariantRows(n); setDirty(true); }} />
                                                ) : (
                                                    <span style={{ fontSize: 13, color: (parseInt(row.quantity, 10) || 0) > 0 ? '#29282D' : '#9ca3af' }}>
                                                        {parseInt(row.quantity, 10) || 0}
                                                    </span>
                                                )}
                                            </td>
                                            <td style={{ padding: '6px 12px' }}>
                                                <input className="sep-input" style={{ width: 130 }} placeholder="e.g. TS-STN-S" value={row.sku}
                                                    onChange={(e) => { const n = [...variantRows]; n[ri] = { ...row, sku: e.target.value }; setVariantRows(n); setDirty(true); }} />
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        <p style={{ fontSize: 12, color: '#6b7280', margin: '8px 0 0' }}>
                            In your shop: {variantOptions.filter(o => o.name.trim() && o.values.length).map(o => o.name).join(' and ')} dropdowns
                            {' '}· {variantRows.length} variant{variantRows.length === 1 ? '' : 's'}
                            {' '}· total stock {variantRows.reduce((n, r) => n + (parseInt(r.quantity, 10) || 0), 0)}
                        </p>

                        {/* One photo per choice of the first option (e.g. per
                            colour). The shop leads with it when that choice is
                            picked. Assignable from the saved gallery only. */}
                        {(() => {
                            const firstOpt = variantOptions.filter(o => o.name.trim() && o.values.length)[0];
                            if (!firstOpt) return null;
                            return (
                                <div style={{ marginTop: 14, background: '#f8fafc', border: '1px solid #eef2f6', borderRadius: 12, padding: '12px 14px' }}>
                                    <div style={{ fontSize: 13, fontWeight: 600, color: '#374151' }}>A photo for each {firstOpt.name}</div>
                                    <p style={{ fontSize: 12, color: '#6b7280', margin: '2px 0 10px' }}>
                                        Optional. When a client picks a {firstOpt.name}, your shop shows its photo first.
                                    </p>
                                    {existingImages.length === 0 ? (
                                        <p style={{ fontSize: 12, color: '#6b7280', margin: 0 }}>
                                            Add photos on the Images tab and save the product. Then you can pick one per {firstOpt.name} here.
                                        </p>
                                    ) : (
                                        <>
                                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
                                                {firstOpt.values.map((val) => (
                                                    <div key={val} style={{ textAlign: 'center' }}>
                                                        <button type="button"
                                                            onClick={() => setImagePickerFor(imagePickerFor === val ? null : val)}
                                                            style={{
                                                                width: 56, height: 56, borderRadius: 10, padding: 0, cursor: 'pointer', overflow: 'hidden',
                                                                border: imagePickerFor === val ? '2px solid #007ea0' : (valueImages[val] ? '1px solid #d1d5db' : '1px dashed #cbd5e1'),
                                                                background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                            }}>
                                                            {valueImages[val]
                                                                ? <img src={valueImages[val]} alt={val} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                                                : <span style={{ fontSize: 20, color: '#9ca3af', lineHeight: 1 }}>+</span>}
                                                        </button>
                                                        <div style={{ fontSize: 11, fontWeight: 600, color: '#374151', marginTop: 4, maxWidth: 64, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{val}</div>
                                                    </div>
                                                ))}
                                            </div>
                                            {imagePickerFor !== null && firstOpt.values.includes(imagePickerFor) && (
                                                <div style={{ marginTop: 10, paddingTop: 10, borderTop: '1px solid #eef2f6' }}>
                                                    <div style={{ fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6 }}>Choose the photo for {imagePickerFor}</div>
                                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                                                        {existingImages.map((img) => (
                                                            <button key={img.id} type="button"
                                                                onClick={() => {
                                                                    setValueImages({ ...valueImages, [imagePickerFor]: img.url });
                                                                    setImagePickerFor(null); setDirty(true);
                                                                }}
                                                                style={{
                                                                    width: 48, height: 48, borderRadius: 8, padding: 0, cursor: 'pointer', overflow: 'hidden',
                                                                    border: valueImages[imagePickerFor] === img.url ? '2px solid #007ea0' : '1px solid #e5e7eb', background: '#fff',
                                                                }}>
                                                                <img src={img.url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                                            </button>
                                                        ))}
                                                        {valueImages[imagePickerFor] && (
                                                            <button type="button"
                                                                onClick={() => {
                                                                    const next = { ...valueImages }; delete next[imagePickerFor];
                                                                    setValueImages(next); setImagePickerFor(null); setDirty(true);
                                                                }}
                                                                style={{ height: 48, borderRadius: 8, padding: '0 12px', cursor: 'pointer', border: '1px solid #e5e7eb', background: '#fff', fontSize: 12, color: '#b91c1c', fontWeight: 600 }}>
                                                                No photo
                                                            </button>
                                                        )}
                                                    </div>
                                                </div>
                                            )}
                                        </>
                                    )}
                                </div>
                            );
                        })()}
                    </div>
                )}
            </div>

            {/* Sell online — list this product in the customer storefront (the
                salon's public booking page Shop). */}
            {notForSale ? (
                <p className="sep-field-hint" style={{ margin: '14px 0 0' }}>Not for sale, so it stays out of your online shop.</p>
            ) : <div className="sep-toggle-row" style={{ borderBottom: 'none' }}>
                <div className="sep-toggle-info">
                    <div className="sep-toggle-title">Sell online</div>
                    <div className="sep-toggle-desc">Show this product in your online shop, for delivery or collection</div>
                </div>
                <button
                    type="button"
                    className={`sep-toggle ${sellOnline === 'yes' ? 'sep-toggle--on' : ''}`}
                    onClick={() => { setSellOnline(sellOnline === 'yes' ? 'no' : 'yes'); setDirty(true); }}
                >
                    <span className="sep-toggle-knob" />
                </button>
            </div>}
        </div>
    );

    const editorContent = (
        <>
            {/* Header — matches sdp-header pattern */}
            <div className="sdp-header">
                <div className="sdp-header-info">
                    <h2 className="sdp-header-name">{productName || (product ? 'Edit Product' : 'New Product')}</h2>
                </div>
                <button className="beu-icon-btn" onClick={handleClose}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                    </svg>
                </button>
            </div>

            {/* Steps */}
            <div className="sep-steps">
                {STEPS.map(step => (
                    <button
                        key={step.id}
                        className={`sep-step ${activeTab === step.id ? 'sep-step--active' : ''}`}
                        onClick={() => setActiveTab(step.id)}
                    >
                        <span className="sep-step-number">{step.number}</span>
                        <span className="sep-step-label">{step.label}</span>
                    </button>
                ))}
            </div>

            {/* Body */}
            <div className="sep-body" key={activeTab}>
                {activeTab === 'details' && renderDetails()}
                {activeTab === 'images' && renderImages()}
                {activeTab === 'pricing' && renderPricing()}
                {activeTab === 'stock' && renderStock()}
            </div>

            {/* Footer. A new product walks the steps in order (Next), then
                saves; an existing one can save from any step. */}
            <div className="sdp-footer">
                <button type="button" className="sdp-btn sdp-btn--neutral" onClick={handleClose}>
                    {inline ? 'Back' : 'Cancel'}
                </button>
                {!productId && stepIndex < STEPS.length - 1 ? (
                    <button type="button" className="sdp-btn sdp-btn--primary" onClick={goNext}>
                        Next
                    </button>
                ) : (
                    <button
                        type="button"
                        className="sdp-btn sdp-btn--primary"
                        onClick={handleSave}
                        disabled={saving}
                    >
                        {saving ? 'Saving...' : 'Save'}
                    </button>
                )}
            </div>
        </>
    );

    if (inline) return editorContent;

    return (
        <SlidePanel ref={slidePanelRef} onClose={onClose} className="sep-panel" width={700}>
            {editorContent}
        </SlidePanel>
    );
};

export default ProductEditorPanel;
